import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { useCompare } from "@/lib/compare-context";
import { api } from "@/lib/api";
import type { Property } from "@/types/property";
import { SafeImage } from "@/components/site/SafeImage";
import { rankPropertyImages } from "@/lib/property-images";

export function CompareBar() {
  const { ids, remove, clear, max } = useCompare();
  const [items, setItems] = useState<Record<string, Property>>({});
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState<number | null>(null);
  const open = ids.length > 0;

  // Publish the tray's live height so other fixed UI (WhatsApp button,
  // cookie banner) can sit above it, and so the in-flow spacer below
  // matches exactly instead of guessing.
  useEffect(() => {
    const el = barRef.current;
    const root = document.documentElement;
    if (!open || !el) {
      root.style.removeProperty("--compare-bar-h");
      return;
    }
    const publish = () => {
      const h = el.offsetHeight;
      setBarHeight(h);
      root.style.setProperty("--compare-bar-h", `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--compare-bar-h");
    };
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    const missing = ids.filter((id) => !items[id]);
    if (missing.length === 0) return;
    Promise.all(
      missing.map((id) =>
        api.getProperty(id).then(
          (p) => [id, p] as const,
          () => null,
        ),
      ),
    ).then((results) => {
      if (cancelled) return;
      setItems((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (r) next[r[0]] = r[1];
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [ids, items]);

  if (!open) return null;

  const params = new URLSearchParams();
  params.set("ids", ids.join(","));
  const compareHref = `/compare?${params.toString()}`;

  return (
    <>
      {/* Spacer so fixed bar doesn't cover footer content */}
      <div
        className="h-[88px] sm:h-[100px]"
        style={barHeight != null ? { height: barHeight } : undefined}
        aria-hidden
      />
      <div
        ref={barRef}
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--line-strong)] bg-[var(--bg-2)]/95 pb-[var(--safe-bottom)] backdrop-blur-md"
      >
      <div className="container-wide flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:py-4">
        <div className="flex items-center gap-3 overflow-x-auto">
          <span className="hidden text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)] sm:inline whitespace-nowrap">
            Comparing · {ids.length}/{max}
          </span>
          <ul className="flex items-center gap-2">
            {ids.map((id) => {
              const p = items[id];
              return (
                <li
                  key={id}
                  className="group relative flex-none border border-[var(--line-strong)] bg-[var(--surface)]"
                >
                  <div className="h-12 w-16 sm:h-14 sm:w-20 overflow-hidden">
                    {p ? (
                      <SafeImage
                        src={rankPropertyImages(p.images, p.id)}
                        alt={p.title}
                        loading="lazy"
                        sizes="80px"
                        widths={[120, 180, 240]}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full animate-pulse bg-[var(--surface-2)]" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(id)}
                    aria-label="Remove from compare"
                    className="absolute -right-1.5 -top-1.5 inline-flex size-7 items-center justify-center bg-[var(--navy)] text-[var(--cream)] outline outline-1 outline-[var(--cream)]/40 hover:bg-[var(--cream)] hover:text-[var(--navy)] lg:size-5"
                  >
                    <X className="size-3.5 lg:size-3" />
                  </button>
                </li>
              );
            })}
            {ids.length < max &&
              Array.from({ length: Math.min(2, max - ids.length) }).map((_, i) => (
                <li
                  key={`ph-${i}`}
                  className="hidden h-12 w-16 flex-none border border-dashed border-[var(--line)] sm:h-14 sm:w-20 sm:block"
                  aria-hidden
                />
              ))}
          </ul>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={clear}
            className="inline-flex h-11 items-center gap-1.5 px-3 text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-[var(--cream)] lg:h-10"
          >
            Clear
          </button>
          <Link
            to={compareHref}
            className={
              ids.length < 2
                ? "inline-flex h-11 cursor-not-allowed items-center gap-2 bg-[var(--surface-2)] px-5 text-[12px] uppercase tracking-[0.22em] text-[var(--text-muted)]"
                : "inline-flex h-11 items-center gap-2 bg-[var(--cream)] px-5 text-[12px] uppercase tracking-[0.22em] text-[var(--navy)] transition-colors hover:bg-[#e6dab0]"
            }
            onClick={(e) => {
              if (ids.length < 2) e.preventDefault();
            }}
            aria-disabled={ids.length < 2}
          >
            Compare {ids.length >= 2 ? `${ids.length} homes` : "(pick 2+)"}
            {ids.length >= 2 && <ArrowRight className="size-4" />}
          </Link>
        </div>
      </div>
    </div>
    </>
  );
}
